import './updateUser.scss';
import Sidebar from '../../components/sidebar/Sidebar';
import Navbar from '../../components/navbar/Navbar';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import { useEffect, useState } from 'react';
import { supabase } from '../../utils/supabase.js';
import { useNavigate, useParams } from 'react-router-dom';
import dbService from '../../services/database';
import Loading from '../../components/Loading/Loading';
import useRolePermissions from '../../hooks/useRolePermissions';
import useStaffDetails from '../../hooks/useStaffDetails';
import ReadOnlyForm from '../../components/ReadOnlyForm/ReadOnlyForm';

const UpdateUser = ({ inputs, title }) => {
  const [file, setFile] = useState(null);
  const [per, setPerc] = useState(null);
  const [data, setData] = useState({
    status: '',
    img: '',
    price: '',
    share: '',
    trust: 'individual',
  });
  const [additionalData, setAdditionalData] = useState({
    price: '',
    shares: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { userId } = useParams();
  const [originalPrice, setOriginalPrice] = useState(0);
  const [originalShares, setOriginalShares] = useState(0);
  const { isReadOnlyRole } = useRolePermissions();
  const { staffDetails } = useStaffDetails();

  // Supabase Storage upload effect
  useEffect(() => {
    if (!file) return;

    const uploadFile = async () => {
      const path = `users/${userId}/${Date.now()}_${file.name}`;
      setPerc(0);

      const { error } = await supabase.storage
        .from('user-images')
        .upload(path, file, { upsert: true, contentType: file.type });

      if (error) {
        console.error('Upload error:', error);
        setErrors((prev) => ({ ...prev, file: 'Error uploading image. Please try again.' }));
        setPerc(null);
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('user-images')
        .getPublicUrl(path);

      setData((prev) => ({ ...prev, img: publicUrl }));
      setErrors((prev) => ({ ...prev, file: null }));
      setPerc(100);
    };

    uploadFile();
  }, [file, userId]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // File Size Validation
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (selectedFile.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        file: 'File size should not exceed 5MB.',
      }));
      return;
    }

    // File Type Validation
    const allowedFormats = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'application/pdf',
    ];
    if (!allowedFormats.includes(selectedFile.type)) {
      setErrors((prev) => ({
        ...prev,
        file: 'Please upload a valid file format (JPEG, JPG, PNG, GIF, PDF).',
      }));
      return;
    }

    // Clear previous errors and set file
    setErrors((prev) => ({ ...prev, file: null }));
    setFile(selectedFile);
  };

  const handleInput = (e) => {
    const { id, value } = e.target;

    if (id === 'additionalPrice') {
      setAdditionalData((prev) => ({ ...prev, price: value }));
      setData((prevData) => ({
        ...prevData,
        price: Number(originalPrice) + Number(value),
      }));
    } else if (id === 'additionalShares') {
      setAdditionalData((prev) => ({ ...prev, shares: value }));
      setData((prevData) => ({
        ...prevData,
        share: Number(originalShares) + Number(value),
      }));
    } else {
      // Handle special field names with hyphens or other characters
      setData((prevData) => ({ ...prevData, [id]: value }));

      if (id === 'trust' && value === 'individual') {
        setData((prevData) => ({
          ...prevData,
          corpName: '',
          doi: '',
          tax: '',
        }));
      }
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // Map data to PostgreSQL format (COMPLETE MAPPING)
      const updateData = {
        // Basic info
        display_name: data.displayName,
        email: data.email,
        phone: data.phone,
        address: data.address,

        // Identity & contact
        ghana_card: data.card,
        next_of_kin: data.kin,
        kin_contact: data['kin-contact'] || '', // Legacy field
        next_of_kin_contact: data['kin-contact'] || '', // Primary field

        // Financial info
        share_amount: data.share,
        share_value: data.price,
        account_number: data.account,
        payment_channel: data.channel,
        branch_code: data.code,

        // Personal info
        date_of_birth: data.dob,
        date_of_purchase: data.dop,
        digital_address: data.digital,
        gender: data.gender,

        // Administrative
        status: data.status,
        img_url: data.img,
        inputter_name: data.inputter,
        added_by: data.addedBy,
        bio: data.bio,
        other_info: data.otherInfo,
        previous_name: data.previousName,
        user_role: data.userRole,

        // Corporation info
        beneficiary_type: data.trust,
        corp_name: data.corpName,
        doi: data.doi,
        tax_id: data.tax,

        // Legacy fields (for backward compatibility)
        dob: data.dob,
        inputter: data.inputter,
      };

      console.log('📤 Sending complete update data to backend:', {
        critical_fields: {
          next_of_kin_contact: updateData.next_of_kin_contact,
          account_number: updateData.account_number,
          digital_address: updateData.digital_address,
          inputter_name: updateData.inputter_name,
          date_of_birth: updateData.date_of_birth,
          date_of_purchase: updateData.date_of_purchase,
        },
        total_fields: Object.keys(updateData).length,
      });

      await dbService.updateUser(userId, updateData);

      if (additionalData.price || additionalData.shares) {
        // Determine created_by with proper fallback chain
        let createdBy = 'System'; // Default fallback

        // Try to use actual staff ID first (for foreign key constraint)
        if (staffDetails?.id) {
          createdBy = staffDetails.id;
        }
        // Fall back to staff username if available
        else if (staffDetails?.username) {
          createdBy = staffDetails.username;
        }
        // Fall back to staff name if available
        else if (staffDetails?.name) {
          createdBy = staffDetails.name;
        }
        // Fall back to inputter name from user data
        else if (data.inputter) {
          createdBy = data.inputter;
        }
        // Fall back to current user inputter name
        else if (data.inputter_name) {
          createdBy = data.inputter_name;
        }

        const transactionData = {
          user_id: userId,
          amount_of_shares: Number(additionalData.shares) || 0,
          transaction_value: Number(additionalData.price) || 0,
          transaction_type: 'bought', // Must be 'bought' or 'sold' as per schema
          created_by: createdBy, // Use fallback chain
          description: 'Additional shares/value added',
          reference_number: `ADD-${Date.now()}`, // Generate a reference number
          payment_method: data.channel || 'bank_transfer', // Use user's payment channel or default
        };

        console.log('📝 Creating transaction with fallback created_by:', {
          created_by: transactionData.created_by,
          fallback_chain: {
            staff_id: staffDetails?.id || 'not available',
            staff_username: staffDetails?.username || 'not available',
            staff_name: staffDetails?.name || 'not available',
            inputter: data.inputter || 'not available',
            inputter_name: data.inputter_name || 'not available',
          },
          staff_details: staffDetails,
          user_id: userId,
        });

        try {
          await dbService.addTransaction(transactionData);
          setAdditionalData({ price: '', shares: '' });
          console.log('✅ Transaction created successfully');
        } catch (transactionError) {
          console.error('❌ Transaction creation failed:', transactionError);

          // If transaction fails due to foreign key constraint, try with inputter fallback
          if (
            transactionError.response?.status === 500 &&
            (transactionError.response?.data?.error?.includes('foreign key') ||
              transactionError.response?.data?.error?.includes('violates'))
          ) {
            console.log('🔄 Retrying transaction with inputter fallback');

            // Retry with inputter name as created_by
            const fallbackTransactionData = {
              ...transactionData,
              created_by:
                data.inputter || data.inputter_name || 'System-User-Update',
            };

            console.log('🔄 Retry transaction data:', {
              original_created_by: transactionData.created_by,
              fallback_created_by: fallbackTransactionData.created_by,
            });

            try {
              await dbService.addTransaction(fallbackTransactionData);
              setAdditionalData({ price: '', shares: '' });
              console.log('✅ Transaction created successfully with fallback');
            } catch (retryError) {
              console.error('❌ Transaction retry also failed:', retryError);
              throw new Error(
                `Transaction creation failed: ${
                  retryError.response?.data?.error || retryError.message
                }`
              );
            }
          } else {
            throw transactionError;
          }
        }
      }

      navigate(-1);
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await dbService.getUserById(userId);
        if (userData) {
          console.log('📥 Raw user data from backend:', userData);

          // Map PostgreSQL fields to component state format (COMPLETE MAPPING)
          const mappedData = {
            ...userData,
            // Basic info mappings
            displayName: userData.display_name,
            membershipCode: userData.membership_code,
            img: userData.img_url,

            // Contact & identity mappings
            card: userData.ghana_card,
            kin: userData.next_of_kin,
            'kin-contact': userData.next_of_kin_contact || userData.kin_contact, // Support both fields

            // Financial mappings
            channel: userData.payment_channel,
            share: userData.share_amount,
            price: userData.share_value,
            code: userData.branch_code,
            account: userData.account_number,

            // Personal info mappings
            dob: userData.date_of_birth
              ? userData.date_of_birth.split('T')[0]
              : userData.dob
              ? userData.dob.split('T')[0]
              : '', // Convert to YYYY-MM-DD
            dop: userData.date_of_purchase
              ? userData.date_of_purchase.split('T')[0]
              : '', // Convert to YYYY-MM-DD
            digital: userData.digital_address,
            gender: userData.gender,

            // Administrative mappings
            inputter: userData.inputter_name || userData.inputter,
            trust: userData.beneficiary_type || 'individual',

            // Corporation mappings
            corpName: userData.corp_name,
            doi: userData.doi,
            tax: userData.tax_id,

            // Additional fields for completeness
            bio: userData.bio,
            otherInfo: userData.other_info,
            previousName: userData.previous_name,
            userRole: userData.user_role,
            addedBy: userData.added_by,

            // Missing form fields extracted from Firebase metadata
            // firstName: userData.firstName || '',
            // lastName: userData.lastName || '',
            cert: userData.cert || '', // Default to empty string instead of undefined
          };

          // console.log('📋 Mapped data for form:', {
          //   card: mappedData.card,
          //   kin: mappedData.kin,
          //   'kin-contact': mappedData['kin-contact'],
          //   channel: mappedData.channel,
          //   account: mappedData.account,
          //   dob: mappedData.dob,
          //   dop: mappedData.dop,
          //   digital: mappedData.digital,
          //   inputter: mappedData.inputter,
          //   gender: mappedData.gender,
          //   firstName: mappedData.firstName,
          //   lastName: mappedData.lastName,
          //   cert: mappedData.cert,
          // });

          // ENHANCED DEBUGGING - Check date formatting
          // console.log('🗓️ DATE DEBUGGING:', {
          //   'Raw date_of_birth': userData.date_of_birth,
          //   'Raw date_of_purchase': userData.date_of_purchase,
          //   'Formatted dob': mappedData.dob,
          //   'Formatted dop': mappedData.dop,
          //   'dob type': typeof mappedData.dob,
          //   'dop type': typeof mappedData.dop,
          //   'dob length': mappedData.dob?.length,
          //   'dop length': mappedData.dop?.length,
          // });

          setData(mappedData);
          setOriginalPrice(userData.share_value || 0);
          setOriginalShares(userData.share_amount || 0);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserData();
  }, [userId]);

  if (isLoading)
    return <Loading inline={true} message="Loading user data..." />;

  return (
    <div className="new">
      <Sidebar />
      <div className="newContainer">
        <Navbar />
        <div className="top">
          <h1>{title}</h1>
        </div>
        <div className="bottom">
          <div className="left">
            <img
              src={
                file
                  ? URL.createObjectURL(file)
                  : data.img ||
                    'https://icon-library.com/images/no-image-icon/no-image-icon-0.jpg'
              }
              alt="Profile"
            />
            <div className="image-upload-hint">
              Click the upload icon to update profile image
            </div>
          </div>
          <div className="right">
            <ReadOnlyForm onSubmit={handleUpdate}>
              <div className="formInput">
                <label htmlFor="file">
                  Profile Image{' '}
                  <DriveFolderUploadOutlinedIcon className="icon" />
                </label>
                <input
                  type="file"
                  id="file"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  autoComplete="off"
                  accept="image/*,application/pdf"
                />
                {errors.file && <div className="error">{errors.file}</div>}
                {per !== null && per < 100 && (
                  <div className="upload-progress">
                    <div
                      className="progress-bar"
                      style={{ width: `${per}%` }}
                    ></div>
                  </div>
                )}
                {per === 100 && (
                  <div className="success">Upload completed!</div>
                )}
              </div>

              <div className="formInput">
                <label htmlFor="trust">Beneficiary Type</label>
                <select
                  id="trust"
                  value={data.trust || 'individual'}
                  onChange={handleInput}
                >
                  <option value="individual">Individual</option>
                  <option value="corporation">Corporation</option>
                </select>
              </div>

              <div className="formInput additional-field">
                <label htmlFor="additionalPrice">Additional Share Value</label>
                <input
                  id="additionalPrice"
                  type="number"
                  placeholder="Enter Additional Share Value (GH₵)"
                  onChange={handleInput}
                  value={additionalData.price || ''}
                  autoComplete="off"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="formInput additional-field">
                <label htmlFor="additionalShares">
                  Additional Number of Shares
                </label>
                <input
                  id="additionalShares"
                  type="number"
                  placeholder="Enter Additional Shares"
                  onChange={handleInput}
                  value={additionalData.shares || ''}
                  autoComplete="off"
                  min="0"
                  step="1"
                />
              </div>

              {inputs.map((input) => (
                <div className="formInput" key={input.id}>
                  <label htmlFor={input.id}>{input.label}</label>
                  {input.type === 'select' && input.options ? (
                    <select
                      id={input.id}
                      onChange={handleInput}
                      value={data[input.id] || ''}
                      autoComplete="off"
                      disabled={input.id === 'price' || input.id === 'share'}
                    >
                      {input.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={input.type}
                      placeholder={input.placeholder}
                      id={input.id}
                      onChange={handleInput}
                      value={data[input.id] || ''}
                      autoComplete="off"
                      disabled={input.id === 'price' || input.id === 'share'}
                    />
                  )}
                </div>
              ))}

              {data.trust === 'corporation' && (
                <div className="corporation-section">
                  <div className="section-title">Corporation Information</div>

                  <div className="formInput">
                    <label htmlFor="corpName">Name of Corporation</label>
                    <input
                      id="corpName"
                      type="text"
                      placeholder="e.g. Kwahu Praso Rural Bank Ltd"
                      onChange={handleInput}
                      value={data.corpName || ''}
                    />
                  </div>

                  <div className="formInput">
                    <label htmlFor="doi">Date of Incorporation</label>
                    <input
                      id="doi"
                      type="date"
                      onChange={handleInput}
                      value={data.doi || ''}
                    />
                  </div>

                  <div className="formInput">
                    <label htmlFor="tax">Tax Identification Number</label>
                    <input
                      id="tax"
                      type="text"
                      placeholder="GH-00-0000"
                      onChange={handleInput}
                      value={data.tax || ''}
                    />
                  </div>
                </div>
              )}

              <button
                type="submit"
                className={`submitBtn ${isReadOnlyRole ? 'disabled' : ''}`}
                disabled={isReadOnlyRole}
              >
                Update Shareholder
              </button>
            </ReadOnlyForm>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateUser;
